import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useTheme } from "@emotion/react";
import { Box, Button, CircularProgress, Grid2 as Grid, Stack, Typography } from "@mui/material";
import { Image, XCircle } from "@phosphor-icons/react";
import useNotiStack from "../../../../hooks/useNotiStack";
import { getFiles, removeImage, uploadImages } from "../../../../config/apisauce";
import { BIO_KEY } from "../../../../enums/profileSetupEnums";
import { GenericIconButton } from "../../../generic/GenericIconButton";
import { addRemoveArray } from "../../../../helpers/arrayHelper";
import { INACTIVE } from "../../../../enums/statusEnums";
import heic2any from 'heic2any';
import imageCompression from "browser-image-compression";

export const UploadImage = (props) => {
  const {
    uploadedImages,
    profilePhoto,
    currentPhotoId,
    sectionValidations,
    setUploadedImages,
    handleSubmit,
    userId,
    setProfilePhoto,
    hideSetProfile = false
  } = props;

  const theme = useTheme();
  const { success, error } = useNotiStack();
  const [isUploadImagesLoading, setIsUploadImagesLoading] = useState(false);
  const hasImagesUploaded = uploadedImages?.length > 0;

  const fetchUploadedImages = async () => {
    const response = await getFiles({ user_id: userId });
    if (response.ok) {
      setUploadedImages(response?.data);
    }
  };

  const handleSelectProfilePreview = (data) => {
    setProfilePhoto(data);
  };

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const compressImage = async (file) => {
    let options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1024,
      useWebWorker: true,
    };
    try {
      const fileExtension = file.name.split('.').pop();

      let compressedFile = await imageCompression(file, options);

      while (compressedFile.size > 200 * 1024) {
        options.maxSizeMB *= 0.9;
        if (options.maxSizeMB < 0.02) break;
        compressedFile = await imageCompression(file, options);
      }
      const compressedFileWithExtension = new File(
        [compressedFile],
        file.name.replace(/.[^/.]+$/, `.${fileExtension}`),
        { type: compressedFile.type }
      );

      return compressedFileWithExtension;
    } catch (error) {
      console.error("Compression failed:", error);
      return file;
    }
  };

  const handleUploadImages = async (currentImages) => {
    setIsUploadImagesLoading(true);
    const formData = new FormData();
    currentImages?.forEach((image) => {
      formData.append('images[]', image);
    });

    formData.append('user_id', userId);
    const response = await uploadImages(formData);
    const responseData = response?.data;
    if (response.ok) {
      setUploadedImages((prev) => (prev && prev.length > 0 ? [...responseData, ...prev] : responseData));
      success("Images have been uploaded!");
    }
    setIsUploadImagesLoading(false);
  };

  const heicToJpeg = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const blob = new Blob([event.target.result], { type: "image/jpeg" });
          const convertedBlob = await heic2any({ blob, toType: "image/jpeg", quality: 0.4 });
          const fileName = file.name.replace(/\.heic$/i, "") + ".jpeg";
          resolve(new File([convertedBlob], fileName, { type: "image/jpeg" }));
        } catch (error) {
          reject(error);
        }
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const handleSelectImage = async (event) => {
    const selectedImages = Array.from(event.target.files);
    const maxSize = 51 * 1024 * 1024; // 50MB limit
    const validImages = [];

    setIsUploadImagesLoading(true);

    for (const file of selectedImages) {
      if (file.size > maxSize) {
        error(`This file: ${file.name} is too large. Upload less than 50MB only.`);
        continue;
      }

      try {
        let processedFile = file;

        if (file.name.toLowerCase().endsWith(".heic")) {
          processedFile = await heicToJpeg(file);
        }

        const compressedFile = await compressImage(processedFile);
        validImages.push(compressedFile);
      } catch (error) {
        console.error("Image processing failed:", error);
        error("Upload failed. Try uploading .jpg, .jpeg, or .png.");
      }
    }

    if (validImages?.length > 0) {
      handleUploadImages(validImages);
    }

    setIsUploadImagesLoading(false);
  };

  const handleSetProfilePhoto = () => {
    handleSubmit({ profile_picture_id: profilePhoto?.id }, BIO_KEY);
    success('Photo has been set as profile picture!');
  };

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      handleSelectImage({ target: { files } });
    }
  }, [handleSelectImage]);

  const handleRemoveImage = async (image) => {
    const { ok } = await removeImage({ id: image?.id, status: INACTIVE });
    if (ok) {
      setUploadedImages((prev) => addRemoveArray(prev, false, image));
      success('Image has been removed!');
    }
  };

  useEffect(() => {
    if (userId) fetchUploadedImages();
  }, [userId]);

  return (
    <Fragment>
      <Grid container spacing={2}>
        <Grid item size={{ xs: 12, sm: 7.6 }}>
          <Stack gap={1.5}>
            <Typography variant="subtitle1" fontWeight={700}>Upload Photos</Typography>
            <Box
              component='label'
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '141px',
                border: '1px dashed #919EAB',
                borderRadius: '10px',
                cursor: 'pointer'
              }}>
              {
                isUploadImagesLoading ? <Stack alignItems={'center'} gap={1}>
                  <CircularProgress size="24px" />
                  <Typography variant="body2" color="grey.dark">Uploading</Typography>
                </Stack> : <Stack sx={{ color: '#919EAB' }} alignItems={'center'}>
                  <Image size={32} />
                  <Typography fontWeight={700}>
                    <span style={{ color: theme.palette.primary.main }}>Upload </span>or drop your images here
                    <input
                      multiple
                      type="file"
                      name="resume"
                      onChange={handleSelectImage}
                      hidden
                      accept={".jpg,.jpeg,.png,.gif,.heic"}
                    />
                  </Typography>
                  {sectionValidations?.includes('images') && <Typography variant="caption" color="error" mt={1}>Upload atleast one image</Typography>}
                </Stack>
              }
            </Box>
            <Stack gap={0.5} color={'grey.dark'} sx={{ fontSize: '10px', lineHeight: 1.7 }}>
              Accepted file types: .jpg, .jpeg, .png, .gif, .heic<br></br>
              Note: burst and live images will be converted to a still image<br></br>
              For best results, upload a square image at least 320x320 pixels.
            </Stack>
            <Typography sx={{ fontWeight: 700, lineHeight: "27px" }}>
              Choose a profile photo by clicking an image below.{" "}
              <Box component="span" sx={{ color: "error.main" }}>
                No nudity allowed.
              </Box>
            </Typography>
            <Box sx={{
              border: '1px solid #CDCDCD',
              borderRadius: '12px',
              padding: { xs: '5px', sm: '8px' },
              backgroundColor: '#FFF'
            }}>
              <Box
                sx={{
                  height: '120px',
                  overflowY: 'auto',
                  ...(!hasImagesUploaded && {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  })
                }}
              >
                <Grid container spacing={0.5}>
                  {
                    hasImagesUploaded ? uploadedImages?.map((uploadedImage, index) => {
                      const isSelected = (profilePhoto?.id === uploadedImage?.id);
                      return (
                        <Grid item key={`uploaded-image-key-${index}`} size={{ xs: 4, sm: 2.4 }}>
                          <Box
                            sx={{
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              border: isSelected && `4px solid ${theme.palette.primary.main}`,
                              height: '90px',
                              width: '90px',
                              backgroundColor: 'background.lightBlue',
                              borderRadius: '16px',
                              flexShrink: 0,
                              cursor: 'pointer',
                              overflow: 'hidden',
                              position: 'relative'
                            }}
                            onClick={() => handleSelectProfilePreview(uploadedImage)}
                          >
                            <img
                              src={uploadedImage?.location}
                              alt="Creator Profile"
                              style={{
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                              loading="lazy"
                            />
                            {
                              !isSelected && <Box display={'flex'} justifyContent={'flex-end'}>
                                <GenericIconButton
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleRemoveImage(uploadedImage);
                                  }}
                                  tooltip={'Remove'}
                                  color={'#FFF'}
                                  icon={<XCircle size={24} weight="fill" style={{ filter: "drop-shadow(2px 4px 6px rgba(0, 0, 0, 0.2))" }} />}
                                />
                              </Box>
                            }
                          </Box>
                        </Grid>
                      );
                    }) : <Typography fontSize={'10px'} color="grey.dark">No uploaded image yet</Typography>
                  }
                </Grid>
              </Box>
            </Box>
          </Stack>
        </Grid>
        <Grid item size={{ xs: 12, sm: 4.4 }}>
          <Box sx={{
            height: hideSetProfile ? '425px' : '372px',
            backgroundColor: !profilePhoto && '#F5F5F5',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {
              profilePhoto ? <img loading="lazy" style={{ width: '250px', height: '100%', borderRadius: '16px', objectFit: 'cover' }} src={profilePhoto?.location} /> : <Typography color={sectionValidations?.includes('profile') ? "error" : "grey.dark"}>No profile photo selected</Typography>
            }
          </Box>
          {
            !hideSetProfile && <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="contained"
                sx={{ width: { xs: '100%', sm: '250px', color: '#FFF' } }}
                disabled={!hasImagesUploaded || currentPhotoId == profilePhoto?.id}
                onClick={() => handleSetProfilePhoto()}
              >
                Set Profile Photo
              </Button>
            </Box>
          }
        </Grid>
      </Grid>
    </Fragment >
  );
};